import React from "react";

interface MenuInfoAlertProps {
  menuInfo: {
    name: string;
    description: string;
    displayText: {
      multiplierText?: string;
      discountText?: string;
      maxPerPersonText?: string;
      itemLimitsText?: string;
    };
  };
  fixedPriceInfo: {
    isFixedPrice: boolean;
    rules: Array<{
      max_items: number;
      price?: number;
      formattedPrice?: string;
    }>;
  };
  hidePrices: boolean;
}

const MenuInfoAlert: React.FC<MenuInfoAlertProps> = ({ menuInfo, fixedPriceInfo, hidePrices }) => {
  const hasAnyInfo = checkHasDisplayInfo(menuInfo, fixedPriceInfo, hidePrices);

  if (!hasAnyInfo) {
    return null;
  }

  return (
    <div
      className="gcw:mx-4 gcw:mb-4 gcw:flex gcw:flex-col gcw:rounded-md gcw:border gcw:bg-light-purple gcw:border-medium-grey gcw:text-black gcw:p-4"
      role="alert"
    >
      {renderFixedPriceInfo(fixedPriceInfo, hidePrices)}
      {renderDisplayTexts(menuInfo, fixedPriceInfo, hidePrices)}
    </div>
  );
};

const checkHasDisplayInfo = (
  menuInfo: MenuInfoAlertProps["menuInfo"],
  fixedPriceInfo: MenuInfoAlertProps["fixedPriceInfo"],
  hidePrices: boolean,
): boolean => {
  return (
    fixedPriceInfo.isFixedPrice ||
    !!menuInfo.displayText.multiplierText ||
    (!hidePrices && !!menuInfo.displayText.discountText) ||
    !!menuInfo.displayText.maxPerPersonText ||
    !!menuInfo.displayText.itemLimitsText
  );
};

const renderFixedPriceInfo = (fixedPriceInfo: MenuInfoAlertProps["fixedPriceInfo"], hidePrices: boolean) => {
  if (!fixedPriceInfo.isFixedPrice) return null;

  return (
    <div className="gcw:mb-2">
      <span className="gcw:font-semibold">You can choose to order:</span>
      {(fixedPriceInfo.rules ?? []).map((rule) => (
        <div key={`${rule.max_items}-${rule.price ?? "no-price"}`} className="gcw:ms-2">
          {rule.max_items} items
          {!hidePrices && rule.formattedPrice && <span> for {rule.formattedPrice}</span>}
        </div>
      ))}
    </div>
  );
};

const renderDisplayTexts = (
  menuInfo: MenuInfoAlertProps["menuInfo"],
  fixedPriceInfo: MenuInfoAlertProps["fixedPriceInfo"],
  hidePrices: boolean,
) => {
  const { displayText } = menuInfo;

  return (
    <>
      {displayText.multiplierText && (
        <div className="gcw:mb-2">
          {fixedPriceInfo.isFixedPrice && <br />}
          <span>{displayText.multiplierText}</span>
        </div>
      )}

      {displayText.maxPerPersonText && (
        <div className="gcw:mb-2">
          <span>{displayText.maxPerPersonText}</span>
        </div>
      )}

      {displayText.itemLimitsText && (
        <div className="gcw:mb-2">
          <span>{displayText.itemLimitsText}</span>
        </div>
      )}

      {displayText.discountText && !hidePrices && (
        <div className="gcw:mb-0">
          <span>{displayText.discountText}</span>
        </div>
      )}
    </>
  );
};

export default MenuInfoAlert;
